import { CONFIG } from '../core/config.js';

export class SearchManager {
	constructor() {
		this.isAmazonLP = window.location.href.includes('/benefits/amazon');
		this.isSocialLP = window.location.href.includes(
			'/start/find-therapist-social/',
		);
		this.contextMap = {
			// Dropdown type mappings
			states: 'State',
			payors: 'Insurance',
			specialties: 'Specialties',
			'type-of-care': 'Type of Care',
			// Search input ID mappings
			'search-states': 'State',
			'search-payors': 'Insurance',
			'search-specialties': 'Specialties',
		};

		this.searchDebounceTimers = new Map();
		this.parsedDataCache = new WeakMap();
		this.debounceDelay = 300;

		this.FORM_FIELD_NAMES = {
			PAYORS: 'payors-options',
			SPECIALTIES: 'specialties-options',
			STATES: 'states-options',
			TYPE_OF_CARE: 'type-of-care-options',
		};

		// Maps a filter input's `name` attribute to its analytics label.
		this.FILTER_LABELS = {
			[this.FORM_FIELD_NAMES.STATES]: 'State',
			[this.FORM_FIELD_NAMES.PAYORS]: 'Insurance',
			[this.FORM_FIELD_NAMES.SPECIALTIES]: 'Specialties',
			[this.FORM_FIELD_NAMES.TYPE_OF_CARE]: 'Type of Care',
		};
	}

	/**
	 * Handle search input blur event for tracking
	 * @param {Event} e - The blur event
	 */
	handleBlur(e) {
		const searchInput = e.target;
		if (
			!searchInput ||
			!searchInput.matches ||
			!searchInput.matches(CONFIG.ELEMENTS.searchInput)
		) {
			return;
		}

		this.trackSearchInput(searchInput);
	}

	/**
	 * Handle search input events with debouncing
	 * @param {Event} e - The input event
	 */
	handleInput(e) {
		const searchInput = e.target;
		if (
			!searchInput.matches ||
			!searchInput.matches(CONFIG.ELEMENTS.searchInput)
		) {
			return;
		}

		const existingTimer = this.searchDebounceTimers.get(searchInput);
		if (existingTimer) {
			clearTimeout(existingTimer);
		}

		const timer = setTimeout(() => {
			this.perform(searchInput);
			this.searchDebounceTimers.delete(searchInput);
		}, this.debounceDelay);

		this.searchDebounceTimers.set(searchInput, timer);
	}

	perform(searchInput) {
		const modal = searchInput.closest(CONFIG.ELEMENTS.dropdownModal);
		if (!modal) {
			return;
		}

		const searchTerm = searchInput.value.trim();
		if (!searchTerm) {
			this.applyCrossFiltering(modal);
			return;
		}

		this.filterOptionsBySearchTerm(modal, searchTerm.toLowerCase());
	}

	filterOptionsBySearchTerm(modal, searchTerm) {
		const options = modal.querySelectorAll(CONFIG.ELEMENTS.option);
		if (options.length === 0) {
			return;
		}

		const hiddenClass = CONFIG.CSS_CLASSES.optionHidden;

		for (const option of options) {
			const checkbox = option.querySelector('input[type="checkbox"]');
			if (!checkbox) {
				continue;
			}

			const searchData = this.getParsedSearchData(checkbox);
			if (!searchData) {
				continue;
			}

			const isSearchMatch = searchData.searchText.includes(searchTerm);
			const isCrossFiltered = !option.classList.contains(hiddenClass);

			const shouldShow = isSearchMatch && isCrossFiltered;
			this.updateOptionDisplay(option, shouldShow);
		}
	}

	getParsedSearchData(checkbox) {
		if (this.parsedDataCache.has(checkbox)) {
			return this.parsedDataCache.get(checkbox);
		}

		const searchData = checkbox.dataset.searchData;
		if (!searchData) {
			return null;
		}

		try {
			const parsed = JSON.parse(searchData);
			this.parsedDataCache.set(checkbox, parsed);
			return parsed;
		} catch {
			// eslint-disable-next-line no-console
			console.warn('Invalid search data for option:', checkbox);
			return null;
		}
	}

	applyCrossFiltering(modal) {
		const options = modal.querySelectorAll(CONFIG.ELEMENTS.option);
		if (options.length === 0) {
			return;
		}

		const hiddenClass = CONFIG.CSS_CLASSES.optionHidden;

		for (const option of options) {
			const isCrossFiltered = !option.classList.contains(hiddenClass);
			this.updateOptionDisplay(option, isCrossFiltered);
		}
	}

	/**
	 * Update option display state
	 * @param {HTMLElement} option     - The option element
	 * @param {boolean}     shouldShow - Whether the option should be visible
	 */
	updateOptionDisplay(option, shouldShow) {
		const currentDisplay = option.style.display;
		const newDisplay = shouldShow ? '' : 'none';

		if (currentDisplay !== newDisplay) {
			option.style.display = newDisplay;
		}
	}

	clear(modal) {
		const searchInput = modal.querySelector(CONFIG.ELEMENTS.searchInput);
		if (searchInput) {
			searchInput.value = '';
		}

		this.applyCrossFiltering(modal);
	}

	/**
	 * Track input click for analytics for Amazon LP and Social LP
	 *
	 * @param {HTMLElement} input - The input element
	 */
	trackInputClick(input) {
		if (!window.SegmentTracking || (!this.isAmazonLP && !this.isSocialLP)) {
			return;
		}

		const dropdownContext = this.getDropdownContextFromElement(input);
		if (!dropdownContext) {
			return;
		}

		const componentName = this.getComponentNameFromForm(input);
		if (!componentName) {
			return;
		}

		const eventName = this.isAmazonLP
			? 'Home Page Hero Filter Clicked'
			: 'Home Page Hero Filter Updated';
		const properties = this.isAmazonLP
			? {
					component_name: componentName,
					label: dropdownContext,
				}
			: {
					component_name: componentName,
					experience_type: 'lp',
					label: dropdownContext,
					selected_value: input.value,
				};

		try {
			window.SegmentTracking.trackButtonClick(eventName, properties);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error tracking input click:', error);
		}
	}

	/**
	 * Track search input for analytics
	 * @param {HTMLInputElement} searchInput - The search input element
	 */
	trackSearchInput(searchInput) {
		const searchTerm = searchInput.value.trim();

		if (!searchTerm || !window.SegmentTracking) {
			return;
		}

		const searchContext = this.getSearchContext(searchInput);
		if (!searchContext) {
			return;
		}

		const componentName = this.getComponentNameFromForm(searchInput);
		if (!componentName) {
			return;
		}

		try {
			window.SegmentTracking.trackUserInput(`${searchContext} Search`, {
				component_name: componentName,
				value: searchTerm,
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error tracking search input:', error);
		}
	}

	/**
	 * Track modal trigger click for analytics
	 * @param {HTMLElement} triggerButton - The modal trigger button element
	 */
	trackModalTriggerClick(triggerButton) {
		if (!window.SegmentTracking) {
			return;
		}

		const dropdownContext = this.getDropdownContextFromElement(triggerButton);
		if (!dropdownContext) {
			return;
		}

		const componentName = this.getComponentNameFromForm(triggerButton);
		if (!componentName) {
			return;
		}

		const properties = {
			component_name: componentName,
		};

		if (this.isAmazonLP) {
			properties.label = dropdownContext;
		}

		try {
			window.SegmentTracking.trackButtonClick(
				this.isAmazonLP ? 'Home Page Hero Filter Clicked' : dropdownContext,
				properties,
			);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error tracking modal trigger click:', error);
		}
	}

	/**
	 * Track tabbed option click for analytics
	 * @param {HTMLElement} tabbedOption - The tabbed option element
	 */
	trackTabbedOptionClick(tabbedOption) {
		if (!window.SegmentTracking) {
			return;
		}

		const componentName = this.getComponentNameFromForm(tabbedOption);
		if (!componentName) {
			return;
		}

		try {
			window.SegmentTracking.trackButtonClick('Type of Care', {
				component_name: componentName,
				value: tabbedOption.value,
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error tracking tabbed option click:', error);
		}
	}

	/**
	 * Track a filter value change in the hero or sticky filter for analytics.
	 *
	 * Fires the "User Inputted" event. The in-page hero filter (scoped to
	 * `[data-search-hero]`) reports a "Landing Page Hero Filter Updated" type.
	 * Any other filter — the sticky header or sticky mobile filter — reports a
	 * "Landing Page Filter Updated" type with an "experience" of "Sticky Filter".
	 * Both include the selected value as "updatedValue".
	 *
	 * @param {HTMLInputElement} input - The checkbox or radio input that changed
	 * @return {void}
	 */
	trackFilterUpdate(input) {
		if (!window.SegmentTracking || !input) {
			return;
		}

		const label = this.FILTER_LABELS[input.name];
		if (!label) {
			return;
		}

		const isHeroFilter = !!input.closest('[data-search-hero]');
		const properties = { label, updatedValue: this.getUpdatedValue(input) };
		if (!isHeroFilter) {
			properties.experience = 'Sticky Filter';
		}

		try {
			window.SegmentTracking.trackUserInput(
				isHeroFilter
					? 'Landing Page Hero Filter Updated'
					: 'Landing Page Filter Updated',
				properties,
			);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error tracking filter update:', error);
		}
	}

	/**
	 * Resolve the selected value(s) for the changed filter. Multi-select fields
	 * (Specialties) report an array of every value currently selected within the
	 * same form; single-select fields report the single selected value.
	 *
	 * @param {HTMLInputElement} input - The checkbox or radio input that changed
	 * @return {string|string[]} The selected value, or array of values for multi-select
	 */
	getUpdatedValue(input) {
		if (!input.classList.contains(CONFIG.CSS_CLASSES.multiSelectInput)) {
			return input.value;
		}

		const form = input.closest(CONFIG.ELEMENTS.form);
		if (!form) {
			return input.value;
		}

		const checked = form.querySelectorAll(
			`input[name="${input.name}"]:checked`,
		);
		return Array.from(checked, (option) => option.value);
	}

	/**
	 * Track form submission with selected values
	 *
	 * @param {HTMLFormElement} form - The form element
	 * @return {void}
	 */
	trackFormSubmit(form) {
		if (!window.SegmentTracking) {
			return;
		}

		const componentName = this.getComponentNameFromForm(form);
		if (!componentName) {
			return;
		}

		const eventName = this.isSocialLP ? 'Search' : 'Filters Form Submit';

		try {
			const formData = this.extractFormData(form);
			window.SegmentTracking.trackFormSubmit(eventName, {
				...formData,
				component_name: componentName,
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error tracking form submission:', error);
		}
	}

	/**
	 * Extract form data in the required format
	 * @param {HTMLFormElement} form - The form element
	 * @return {Object} Form data object
	 */
	extractFormData(form) {
		const checkedInputs = form.querySelectorAll(
			'input[type="checkbox"]:checked, input[type="radio"]:checked',
		);

		if (checkedInputs.length === 0) {
			return {
				state: null,
				insurance: null,
				specialties: [],
				typeOfCare: null,
			};
		}

		const formData = {
			state: null,
			insurance: null,
			specialties: [],
			typeOfCare: null,
		};

		for (const input of checkedInputs) {
			const name = input.name;
			const value = input.value;

			switch (name) {
				case this.FORM_FIELD_NAMES.STATES:
					formData.state = value;
					break;
				case this.FORM_FIELD_NAMES.PAYORS:
					formData.insurance = value;
					break;
				case this.FORM_FIELD_NAMES.SPECIALTIES:
					formData.specialties.push(value);
					break;
				case this.FORM_FIELD_NAMES.TYPE_OF_CARE:
					formData.typeOfCare = value;
					break;
			}
		}

		return formData;
	}

	/**
	 * Get component name from form data attribute
	 * @param {HTMLElement} element - Any element within the form
	 * @return {string|null} The component name from data-segment-component-name attribute or null
	 */
	getComponentNameFromForm(element) {
		const form = element.closest(CONFIG.ELEMENTS.form);
		if (!form) {
			return null;
		}

		return form.getAttribute('data-segment-component-name');
	}

	/**
	 * Get dropdown context from any element within a dropdown
	 * @param {HTMLElement} element - Any element within a dropdown
	 * @return {string|null} The dropdown context (State/Insurance/Specialties) or null
	 */
	getDropdownContextFromElement(element) {
		const dropdown = element.closest(CONFIG.ELEMENTS.dropdown);
		if (!dropdown) {
			return null;
		}

		const dropdownType = dropdown.getAttribute(
			'data-search-filter-form-dropdown',
		);
		return dropdownType ? this.contextMap[dropdownType] || null : null;
	}

	/**
	 * Determine the search context based on the search input ID
	 * @param {HTMLInputElement} searchInput - The search input element
	 * @return {string|null} The search context (State/Insurance/Specialties) or null
	 */
	getSearchContext(searchInput) {
		const inputId = searchInput.id;
		if (!inputId || !this.contextMap[inputId]) {
			return null;
		}
		return this.contextMap[inputId];
	}

	cleanup() {
		this.searchDebounceTimers.forEach((timer) => clearTimeout(timer));
		this.searchDebounceTimers.clear();
		this.parsedDataCache = new WeakMap();
	}
}
